import { useState, useCallback, useMemo } from 'react'
import { DocumentUploadCard } from '@/componentsV1/FileUpload/DocumentUploadCard'
import FileUploadModal, {
  UploadedFile,
} from '@/componentsV1/FileUpload/FileUploadModal'
import { NotRelevantModal } from '../../../end-of-year-review/not-relevant-modal'
import { IDocumentsStepContext } from '../../../end-of-year-review/types'
import { UserDocument } from '@/features/user/your-practice/documents/lib/userDocuments.slice'
import { UploadDocumentType } from '@/constants/businessConstants'
import { useDocuments } from '../../../hooks/use-documents'
import { useNotRelevantDocuments } from '../../../hooks/use-not-relevant-documents'
import { getDocumentReuploadStatus } from '../../../hooks'
import {
  getInstructionsLink,
  getMatchingMerchants,
} from '../../../end-of-year-review/processing-fees-documents/documents-map'
import { handleDocumentDeletion } from '../document-deletion.helpers'
import './styles.scss'
import { UserProcessingFee } from '@/features/bookkeeping/end-of-year-review/actions'

export interface ProcessingFeesDocumentProps {
  stepContext: IDocumentsStepContext | undefined
  setStepContext: (context: IDocumentsStepContext) => void
  processingFees: UserProcessingFee[] // Processing fees data to extract merchants from
  userDocuments: UserDocument[]
  isFollowUp: boolean
  taxYear: number
  stepStatus?: string
}

/**
 * ProcessingFeesDocument - Reusable component for processing fees document workflow
 *
 * This component handles merchant-based document uploads for payment processors.
 * Unlike provider-based components (Payroll, Contractor), this uses merchants
 * (payment processors like Stripe, Square) instead of payroll providers.
 *
 * Key differences from provider-based components:
 * - Uses `processingFees` prop to extract merchants
 * - Merchants are stored in the `provider` field of Document objects
 * - Each merchant can have multiple document types (e.g., Stripe has "Balance Report" and "Payout Reconciliation Report")
 * - Uses merchant-specific instructions links and document descriptions
 *
 * @example
 * ```tsx
 * <ProcessingFeesDocument
 *   stepContext={feesContext}
 *   setStepContext={handleSetFeesContext}
 *   processingFees={processingFeesData}
 *   userDocuments={documents}
 *   isFollowUp={false}
 *   taxYear={2024}
 * />
 * ```
 */
export const ProcessingFeesDocument = ({
  stepContext,
  setStepContext,
  processingFees,
  userDocuments,
  isFollowUp,
  taxYear,
  stepStatus,
}: ProcessingFeesDocumentProps) => {
  // Memoize merchant extraction to prevent infinite loops
  const merchants = useMemo(
    () => getMatchingMerchants(String(taxYear), processingFees, []),
    [taxYear, processingFees]
  )

  const [isFileUploadModalOpen, setIsFileUploadModalOpen] = useState(false)
  const [fileUpload, setFileUpload] = useState<{
    merchantName: string
    documentCategoryName: string
    documentName: string
    documentCategoryNameInternal?: string
    type?: UploadDocumentType
  } | null>(null)

  // EXISTING HOOK: Provides utility functions for document filtering & status
  const { filterDocuments, getStatus } = useDocuments({
    stepContextDocuments: stepContext?.documents,
    userDocuments,
  })

  // NEW HOOK: Manages "not relevant" workflow
  const { isMarkedNotRelevant, handleNotRelevant, notRelevantModal } =
    useNotRelevantDocuments(stepContext, setStepContext)

  // Handle upload button click
  const handleFileUpload = useCallback(
    (
      merchantName?: string,
      documentCategoryName?: string,
      documentName?: string,
      documentCategoryNameInternal?: string,
      type?: UploadDocumentType
    ) => {
      setIsFileUploadModalOpen(true)

      if (merchantName && documentCategoryName && documentName) {
        setFileUpload({
          merchantName,
          documentCategoryName,
          documentName,
          documentCategoryNameInternal,
          type,
        })
      } else {
        setFileUpload(null)
      }
    },
    []
  )

  // Handle file upload completion
  const onFileUploaded = useCallback(
    (uploadedFile: UploadedFile) => {
      if (!fileUpload) {
        // Handle other documents (not specific to a merchant)
        const otherDocumentIds = [
          ...(stepContext?.otherDocumentIds ?? []),
          uploadedFile.id,
        ]
        setStepContext({ ...(stepContext ?? {}), otherDocumentIds })
      } else {
        // Handle merchant-specific documents
        const documentId = uploadedFile.id
        const categoryCode = fileUpload.documentCategoryName
        const name = fileUpload.documentName
        const provider = fileUpload.merchantName // Merchant name stored in provider field

        // Find the original document that needs re-upload (if it exists)
        const originalDocument = stepContext?.documents?.find(
          (doc) =>
            doc.categoryCode === categoryCode &&
            doc.provider === provider &&
            doc.name === name
        )

        const documents = [
          ...(stepContext?.documents?.filter(
            (doc) =>
              !(
                doc.categoryCode === categoryCode &&
                doc.provider === provider &&
                doc.name === name
              )
          ) ?? []),
          {
            documentId,
            categoryCode,
            name,
            provider, // Merchant name
            requestReUpload:
              originalDocument?.requestReUpload ??
              (isFollowUp ? true : undefined),
            adminNote: originalDocument?.adminNote,
            updatedByUser: true,
          },
        ]
        setStepContext({ ...(stepContext ?? {}), documents })
      }
      setIsFileUploadModalOpen(false)
    },
    [fileUpload, stepContext, setStepContext, isFollowUp]
  )

  // Handle document deletion - preserve requestReUpload state in follow-up mode
  const handleDocumentDeleted = useCallback(
    (deletedDocumentId: number) => {
      const documents = handleDocumentDeletion(
        stepContext?.documents,
        (doc) => doc.documentId === deletedDocumentId,
        isFollowUp,
        { preserveName: true, preserveProvider: true }
      )
      setStepContext({ ...(stepContext ?? {}), documents })
    },
    [stepContext, setStepContext, isFollowUp]
  )

  // For followup: Check if step requires user action - only render section if status is user_action_required
  if (isFollowUp && stepStatus !== 'user_action_required') {
    return null
  }

  return (
    <div className="processing-fees-document">
      {/* Embedded V1 dialog specific to processing fees documents */}
      <FileUploadModal
        close={() => setIsFileUploadModalOpen(false)}
        documentType={fileUpload?.type || UploadDocumentType.OTHER}
        categoryInternalName={
          fileUpload?.documentCategoryName &&
          fileUpload?.documentCategoryName === 'mix_documents'
            ? undefined
            : fileUpload?.documentCategoryName
        }
        open={isFileUploadModalOpen}
        userFacing
        isUploadOnly={
          fileUpload?.documentCategoryName === 'mix_documents' ? false : true
        }
        setUploadedFile={onFileUploaded}
        isMixedType={
          fileUpload?.documentCategoryName === 'mix_documents' ? true : false
        }
        internalCategoryNameFlag={fileUpload?.documentCategoryNameInternal}
        year={taxYear.toString()}
      />

      {/* Not relevant confirmation modal */}
      <NotRelevantModal {...notRelevantModal} />

      <h2>Processing Fees</h2>

      <div className="processing-fees-document__cards">
        {merchants.map(
          ({
            id,
            merchantName,
            documentCategoryName,
            documentName,
            documentCategoryNameInternal,
            type,
          }) => (
            <DocumentUploadCard
              key={`${id}-${documentName}`}
              name={`${merchantName} - ${documentName}`}
              description="This captures income, processing fees, and their timing."
              explanation={
                merchantName.includes('Stripe')
                  ? 'If you only use Stripe through SimplePractice, select Not Relevant.'
                  : ''
              }
              href={getInstructionsLink(
                documentCategoryName,
                merchantName,
                taxYear.toString()
              )}
              notRelevant={isMarkedNotRelevant(
                documentCategoryName,
                merchantName,
                documentName
              )}
              setChecked={() =>
                handleNotRelevant(
                  documentCategoryName,
                  merchantName,
                  documentName
                )
              }
              status={getStatus(
                documentCategoryName,
                merchantName,
                isMarkedNotRelevant,
                documentName
              )}
              documents={filterDocuments(
                documentCategoryName,
                merchantName,
                documentName
              )}
              handleUploadBtn={() =>
                handleFileUpload(
                  merchantName,
                  documentCategoryName,
                  documentName,
                  documentCategoryNameInternal,
                  type
                )
              }
              isFollowUp={isFollowUp}
              isRequiredReviewed={getDocumentReuploadStatus(
                stepContext,
                documentCategoryName,
                merchantName,
                isFollowUp,
                documentName
              )}
              onDocumentDeleted={handleDocumentDeleted}
            />
          )
        )}

        {/* Other documents */}
        <DocumentUploadCard
          name="Other documents"
          description="Upload any other documents you'd like to share with the bookkeeping team here."
          status="disabled"
          documents={(userDocuments ?? []).filter((doc) =>
            (stepContext?.otherDocumentIds ?? []).includes(doc.id)
          )}
          handleUploadBtn={() => handleFileUpload()}
        />
      </div>
    </div>
  )
}
